<template>
  <div class="select-input" ref="container">
    <!-- Preview Mode (when not focused) -->
    <div
      v-if="!isOpen"
      class="select-preview"
      :class="{ disabled: disabled }"
      @click="!disabled && openDropdown()"
      :tabindex="disabled ? -1 : 0"
      @keydown.enter="!disabled && openDropdown()"
      @keydown.space.prevent="!disabled && openDropdown()"
    >
      <span v-if="displayText" class="preview-text">{{ displayText }}</span>
      <span v-else class="placeholder-text">{{ placeholder }}</span>
    </div>

    <!-- Input Mode (when focused) -->
    <div v-else class="select-active">
      <input
        ref="searchInput"
        v-model="searchTerm"
        type="text"
        class="search-input"
        :placeholder="placeholder"
        @input="handleSearch"
        @keydown.esc="closeDropdown"
        @blur="handleBlur"
      />
    </div>

    <!-- Dropdown Menu (teleported to body) -->
    <Teleport to="body">
      <div
        v-if="isOpen && dropdownPosition"
        class="dropdown-menu"
        :style="dropdownStyle"
      >
        <div v-if="filteredOptions.length === 0" class="dropdown-item empty-item">
          No options found
        </div>
        <button
          v-else
          v-for="option in filteredOptions"
          :key="option.value"
          class="dropdown-item"
          :class="{ selected: isSelected(option.value), disabled: option.disabled }"
          :disabled="option.disabled"
          @mousedown.prevent="!option.disabled && selectOption(option)"
        >
          <span class="item-content">
            <span class="item-text">{{ option.label }}</span>
            <span v-if="option.subtext" class="item-subtext">{{ option.subtext }}</span>
          </span>
          <span v-if="multiSelect && isSelected(option.value)" class="checkmark">✓</span>
        </button>
      </div>
    </Teleport>
  </div>
</template>

<script>
export default {
  name: 'SelectInput',
  props: {
    modelValue: {
      type: [String, Number, Array],
      default: null
    },
    options: {
      type: Array,
      required: true,
      // Array of { value: string|number, label: string }
      validator: (options) => {
        return options.every(opt =>
          Object.prototype.hasOwnProperty.call(opt, 'value') &&
          Object.prototype.hasOwnProperty.call(opt, 'label')
        )
      }
    },
    multiSelect: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'Select...'
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: false,
      searchTerm: '',
      dropdownPosition: null
    }
  },
  computed: {
    dropdownStyle() {
      if (!this.dropdownPosition) return {}
      return {
        position: 'fixed',
        left: `${this.dropdownPosition.left}px`,
        width: `${this.dropdownPosition.width}px`,
        top: `${this.dropdownPosition.top}px`,
        maxHeight: `${this.dropdownPosition.maxHeight}px`
      }
    },
    filteredOptions() {
      if (!this.searchTerm) {
        return this.options
      }
      const search = this.searchTerm.toLowerCase()
      return this.options.filter(option =>
        option.label.toLowerCase().includes(search) ||
        (option.subtext && option.subtext.toLowerCase().includes(search))
      )
    },
    displayText() {
      if (this.multiSelect) {
        if (!this.modelValue || this.modelValue.length === 0) {
          return ''
        }
        const count = this.modelValue.length
        if (count === 1) {
          const selected = this.options.find(opt => opt.value === this.modelValue[0])
          return selected ? selected.label : ''
        }
        return `${count} values selected`
      } else {
        if (!this.modelValue) {
          return ''
        }
        const selected = this.options.find(opt => opt.value === this.modelValue)
        return selected ? selected.label : ''
      }
    }
  },
  mounted() {
    window.addEventListener('scroll', this.updateDropdownPosition, true)
    window.addEventListener('resize', this.updateDropdownPosition)
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.updateDropdownPosition, true)
    window.removeEventListener('resize', this.updateDropdownPosition)
  },
  methods: {
    openDropdown() {
      this.isOpen = true
      this.searchTerm = ''
      this.updateDropdownPosition()
      this.$nextTick(() => {
        this.$refs.searchInput?.focus()
      })
    },
    closeDropdown() {
      this.isOpen = false
      this.searchTerm = ''
      this.dropdownPosition = null
    },
    updateDropdownPosition() {
      if (!this.isOpen || !this.$refs.container) return

      const rect = this.$refs.container.getBoundingClientRect()
      const gap = 4
      const bottomPadding = 10
      const viewportHeight = window.innerHeight

      const top = rect.bottom + gap
      const availableSpace = viewportHeight - top - bottomPadding
      const maxHeight = Math.max(availableSpace, 100)

      this.dropdownPosition = {
        top,
        left: rect.left,
        width: rect.width,
        maxHeight
      }
    },
    handleBlur() {
      // Delay to allow click events to fire
      setTimeout(() => {
        this.closeDropdown()
      }, 200)
    },
    handleSearch() {
      // Client-side filtering via computed property
    },
    isSelected(value) {
      if (this.multiSelect) {
        return this.modelValue && this.modelValue.includes(value)
      }
      return this.modelValue === value
    },
    selectOption(option) {
      if (this.multiSelect) {
        let newValue = [...(this.modelValue || [])]
        const index = newValue.indexOf(option.value)
        if (index > -1) {
          newValue.splice(index, 1)
        } else {
          newValue.push(option.value)
        }
        this.$emit('update:modelValue', newValue)
      } else {
        this.$emit('update:modelValue', option.value)
        this.closeDropdown()
      }
    }
  }
}
</script>

<style scoped>
.select-input {
  width: 100%;
}

/* Preview Mode Styles */
.select-preview {
  width: 100%;
  padding: 4px 12px;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
  background-color: #fff;
  border: 1px solid #d1d5db;
  border-radius: var(--border-radius);
  transition: border-color 0.15s ease-in-out;
  cursor: pointer;
  height: 31px;
  display: flex;
  align-items: center;
}

.select-preview:hover {
  border-color: #9ca3af;
}

.select-preview:focus {
  outline: none;
  border-color: #4338ca;
}

.select-preview.disabled {
  background-color: #f9fafb;
  color: #9ca3af;
  cursor: not-allowed;
  pointer-events: none;
}

.select-preview.disabled .preview-text,
.select-preview.disabled .placeholder-text {
  color: #9ca3af;
}

.preview-text {
  font-size: 14px;
  color: #333;
  line-height: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.placeholder-text {
  color: #9ca3af;
  font-size: 14px;
}

/* Active Mode Styles */
.search-input {
  width: 100%;
  padding: 4px 12px;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
  background-color: #fff;
  border: 1px solid #4338ca;
  border-radius: var(--border-radius);
  transition: border-color 0.15s ease-in-out;
  height: 31px;
}

.search-input:focus {
  outline: none;
}

.search-input::placeholder {
  color: #9ca3af;
}

/* Dropdown Menu (Teleported) */
.dropdown-menu {
  background: white;
  border: 1px solid #d1d5db;
  border-radius: var(--border-radius);
  max-height: 300px;
  overflow-y: auto;
  z-index: 10000;
}

.dropdown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  width: 100%;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s;
  font-size: 14px;
  color: #333;
}

.dropdown-item:hover {
  background-color: #f3f4f6;
}

.dropdown-item.selected {
  background-color: #e0e7ff;
}

.dropdown-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.empty-item {
  color: #9ca3af;
  font-style: italic;
  cursor: default;
}

.empty-item:hover {
  background-color: transparent;
}

.item-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.item-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-subtext {
  font-size: 12px;
  color: #b0b5bc;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.checkmark {
  color: #4338ca;
  font-weight: bold;
  font-size: 16px;
}
</style>
